import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { useAsyncAction } from "./useAsyncAction";

export { useAsyncAction };

// The fixed set of primitives, each doing one thing (APP_TEMPLATE.md §8). Anything that needs a
// new primitive probably needs one of these with a prop instead.

// ── AsyncButton ────────────────────────────────────────────────────────────────────────────

interface AsyncButtonProps {
  onClick: () => Promise<unknown>;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  /** Shown as a tooltip when disabled, so a dead control always says why. */
  disabledReason?: string;
  title?: string;
  type?: "button" | "submit";
}

export function AsyncButton({
  onClick,
  children,
  className = "btn",
  disabled,
  disabledReason,
  title,
  type = "button",
}: AsyncButtonProps) {
  const [run, busy] = useAsyncAction(onClick);

  return (
    <button
      type={type}
      className={className}
      disabled={disabled || busy}
      title={disabled && disabledReason ? disabledReason : title}
      onClick={() => void run()}
    >
      {busy && <span className="btn-spinner" aria-hidden />}
      {children}
    </button>
  );
}

// ── IconButton ─────────────────────────────────────────────────────────────────────────────

interface IconButtonProps {
  onClick: () => void;
  label: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export function IconButton({
  onClick,
  label,
  children,
  className = "btn ghost icon",
  disabled,
  disabledReason,
}: IconButtonProps) {
  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      title={disabled && disabledReason ? disabledReason : label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────────────────────

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

export function Modal({ title, onClose, children, footer, wide }: ModalProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={wide ? "modal wide" : "modal"} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2 className="page-title" style={{ fontSize: "var(--size-lg)" }}>
            {title}
          </h2>
          <IconButton onClick={onClose} label="Close">
            <X size={16} />
          </IconButton>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

interface ConfirmModalProps {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<unknown>;
  onCancel: () => void;
  danger?: boolean;
}

export function ConfirmModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  danger,
}: ConfirmModalProps) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
          <AsyncButton className={danger ? "btn danger" : "btn primary"} onClick={onConfirm}>
            {confirmLabel}
          </AsyncButton>
        </>
      }
    >
      {body}
    </Modal>
  );
}

// ── Notes and states ───────────────────────────────────────────────────────────────────────

export function ErrorNote({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="error-note" role="alert">
      <span className="error-note-title">{title}</span>
      {detail && <span className="field-help">{detail}</span>}
    </div>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return <p className="notice">{children}</p>;
}

/** An empty screen is an invitation to act, so it always carries the next step. */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <p style={{ color: "var(--text-dim)" }}>{title}</p>
      {hint && <p className="field-help">{hint}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ height = 16, width = "100%" }: { height?: number; width?: number | string }) {
  return <div className="skeleton" style={{ height, width }} aria-hidden />;
}

/** Reads a message out of anything thrown, without ever rendering "[object Object]". */
export function describeError(error: unknown): { title: string; detail?: string } {
  if (error && typeof error === "object" && "message" in error) {
    const withDetail = error as { message: string; detail?: string };
    return { title: withDetail.message, detail: withDetail.detail };
  }
  return { title: "Something went wrong." };
}
